import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSimulation } from "@/context/SimulationContext";
import { toast } from "sonner";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { money, statusLabel, statusColor } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Users, Ticket, ShoppingCart, TrendingUp, Ban, CheckCircle2, UserPlus, SlidersHorizontal, Trash2, ArrowUpCircle, Search, Eye, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function Metric({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tint}`}><Icon size={20} /></div>
      <p className="text-sm text-neutral-500">{label}</p>
      <p className="font-display font-extrabold text-2xl mt-1">{value}</p>
    </div>
  );
}

const emptyCreate = { email: "", name: "", max_active_raffles: 1, max_numbers_per_raffle: 100 };

const roleInfo = (role) => role === "assistant"
  ? { label: "Asistente", cls: "bg-violet-100 text-violet-700", key: "assistant" }
  : { label: "Organizador", cls: "bg-[#E6F7FE] text-[#0092CC]", key: "organizer" };

function RoleBadge({ role, uid }) {
  const info = roleInfo(role);
  return (
    <span data-testid={`role-badge-${uid}`} data-role={info.key}
      className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${info.cls}`}>
      {info.label}
    </span>
  );
}

const PLAN_LABELS = { free: "Free", full: "Full", pro: "Pro", custom: "Custom" };
const PLAN_CLS = {
  free: "bg-neutral-100 text-neutral-600",
  full: "bg-sky-100 text-sky-700",
  pro: "bg-amber-100 text-amber-700",
  custom: "bg-emerald-100 text-emerald-700",
};

function PlanBadge({ plan, uid }) {
  const p = (plan || "free").toLowerCase();
  return (
    <span data-testid={`plan-badge-${uid}`} data-plan={p}
      className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${PLAN_CLS[p] || PLAN_CLS.free}`}>
      {PLAN_LABELS[p] || "Free"}
    </span>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { startSim } = useSimulation();
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [creating, setCreating] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [upReqs, setUpReqs] = useState([]);
  const [rSearch, setRSearch] = useState("");
  const [adminRaffles, setAdminRaffles] = useState([]);
  const [delRaffle, setDelRaffle] = useState(null);
  const [delRaffleLoading, setDelRaffleLoading] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  const [rFilter, setRFilter] = useState("all");
  const [reactRaffle, setReactRaffle] = useState(null);
  const [reactLoading, setReactLoading] = useState(false);

  const loadRaffles = async (search = "") => {
    const res = await api.get("/admin/raffles", { params: search ? { search } : {} });
    setAdminRaffles(res.data);
  };
  useEffect(() => { loadRaffles(); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadRaffles(rSearch), 350);
    return () => clearTimeout(t);
  }, [rSearch]);

  const confirmDeleteRaffle = async () => {
    if (!delRaffle) return;
    setDelRaffleLoading(true);
    try {
      await api.delete(`/raffles/${delRaffle.id}`);
      toast.success("Rifa eliminada");
      setDelRaffle(null);
      loadRaffles(rSearch);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar la rifa");
    } finally { setDelRaffleLoading(false); }
  };

  const shownRaffles = rFilter === "all" ? adminRaffles : adminRaffles.filter((r) => r.status === rFilter);

  const confirmReactivate = async () => {
    if (!reactRaffle) return;
    setReactLoading(true);
    try {
      const res = await api.post(`/raffles/${reactRaffle.id}/reactivate`);
      toast.success(`${reactRaffle.status === "closed" ? "Venta reanudada" : "Evento reactivado"} (${statusLabel(res.data.status)})`);
      setReactRaffle(null);
      loadRaffles(rSearch);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reactivar el evento");
    } finally { setReactLoading(false); }
  };

  const load = async () => {
    const [m, o, req, up] = await Promise.all([
      api.get("/admin/metrics"),
      api.get("/admin/organizers"),
      api.get("/admin/access-requests"),
      api.get("/admin/upgrade-requests"),
    ]);
    setMetrics(m.data);
    setOrgs(o.data);
    setReqs(req.data);
    setUpReqs(up.data);
    window.dispatchEvent(new Event("refresh-pending-counts"));
  };
  useEffect(() => { load(); }, []);

  const resolveUpgrade = async (tenantId) => {
    try { await api.post(`/admin/upgrade-requests/${tenantId}/resolve`); toast.success("Solicitud marcada como resuelta"); load(); }
    catch (e) { toast.error("Error"); }
  };
  const openEditByTenant = (tenantId) => {    const org = orgs.find((o) => o.user_id === tenantId);
    if (org) setEditOrg({ ...org });
    else toast.error("Organizador no encontrado");
  };

  const createOrg = async () => {
    if (!createForm.email.trim()) return toast.error("Ingresa el correo del administrador");
    setCreating(true);
    try {
      await api.post("/admin/organizers", {
        email: createForm.email.trim(),
        name: createForm.name.trim(),
        max_active_raffles: Number(createForm.max_active_raffles) || 1,
        max_numbers_per_raffle: Number(createForm.max_numbers_per_raffle) || 100,
      });
      toast.success("Administrador creado. Se le envió un correo de bienvenida.");
      setCreateForm(emptyCreate);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el administrador");
    } finally { setCreating(false); }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/admin/organizers/${editOrg.user_id}`, {
        name: editOrg.name,
        plan: editOrg.plan || "free",
        custom_perms: editOrg.plan === "custom" ? (editOrg.custom_perms || {}) : undefined,
        max_active_raffles: Number(editOrg.max_active_raffles) || 1,
        max_numbers_per_raffle: Number(editOrg.max_numbers_per_raffle) || 100,
        max_assistants: editOrg.max_assistants != null ? Number(editOrg.max_assistants) : undefined,
        max_photos: editOrg.max_photos != null ? Number(editOrg.max_photos) : undefined,
      });
      toast.success("Límites actualizados");
      setEditOrg(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally { setSavingEdit(false); }
  };

  const approveReq = async (uid) => {
    try { await api.post(`/admin/organizers/${uid}/activate`); toast.success("Cuenta habilitada. Correo de bienvenida enviado."); load(); }
    catch (e) { toast.error("Error"); }
  };
  const rejectReq = async (uid) => {
    try { await api.post(`/admin/organizers/${uid}/reject`); toast.success("Solicitud rechazada. Correo enviado."); load(); }
    catch (e) { toast.error("Error"); }
  };

  const confirmDelete = async () => {
    if (!deleteOrg) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/organizers/${deleteOrg.user_id}`);
      toast.success("Administrador eliminado permanentemente");
      setDeleteOrg(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    } finally { setDeleting(false); }
  };

  const toggle = async (org) => {
    try {
      if (org.status === "suspended") await api.post(`/admin/organizers/${org.user_id}/activate`);
      else await api.post(`/admin/organizers/${org.user_id}/suspend`);
      toast.success("Actualizado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const q = orgSearch.trim().toLowerCase();
  const filteredOrgs = q
    ? orgs.filter((o) => (o.name || "").toLowerCase().includes(q) || (o.email || "").toLowerCase().includes(q))
    : orgs;

  return (
    <DashboardLayout title="Super-Admin">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric icon={Users} label="Organizadores" value={metrics?.total_organizers ?? "—"} tint="bg-[#E6F7FE] text-[#00AEEF]" />
        <Metric icon={Ticket} label="Rifas totales" value={metrics?.total_raffles ?? "—"} tint="bg-neutral-900/5 text-neutral-800" />
        <Metric icon={ShoppingCart} label="Pedidos" value={metrics?.total_orders ?? "—"} tint="bg-amber-50 text-amber-600" />
        <Metric icon={TrendingUp} label="Números vendidos" value={metrics?.total_numbers_sold ?? "—"} tint="bg-sky-50 text-sky-600" />
      </div>

      {/* Crear administrador */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] mb-8" data-testid="create-admin-card">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={18} className="text-[#00AEEF]" />
          <h2 className="font-display font-bold text-lg">Crear administrador</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">Solo tú puedes crear administradores. El acceso se habilita al iniciar sesión con ese correo de Google.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-sm">Correo (Gmail) *</Label>
            <Input data-testid="new-admin-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="admin@gmail.com" className="mt-1 rounded-lg" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm">Nombre</Label>
            <Input data-testid="new-admin-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Nombre del organizador" className="mt-1 rounded-lg" />
          </div>
          <div>
            <Label className="text-sm">Máx. rifas activas</Label>
            <Input data-testid="new-admin-max-raffles" type="number" min={1} value={createForm.max_active_raffles} onChange={(e) => setCreateForm({ ...createForm, max_active_raffles: e.target.value })} className="mt-1 rounded-lg" />
          </div>
          <div>
            <Label className="text-sm">Máx. números por rifa</Label>
            <Input data-testid="new-admin-max-numbers" type="number" min={1} value={createForm.max_numbers_per_raffle} onChange={(e) => setCreateForm({ ...createForm, max_numbers_per_raffle: e.target.value })} className="mt-1 rounded-lg" />
          </div>
          <div className="flex items-end">
            <Button data-testid="create-admin-btn" onClick={createOrg} disabled={creating} className="w-full h-10 rounded-xl bg-[#00AEEF] hover:bg-[#0092CC]">
              {creating ? "Creando..." : "Crear"}
            </Button>
          </div>
        </div>
      </div>

      <h2 className="font-display font-bold text-xl mb-4">Solicitudes de acceso</h2>
      <div className="mb-8" data-testid="access-requests">
        {reqs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 text-neutral-400 text-sm">No hay solicitudes pendientes.</div>
        ) : (
          <div className="space-y-3">
            {reqs.map((o) => (
              <div key={o.user_id} data-testid={`req-${o.user_id}`} className="bg-white rounded-2xl border border-amber-200 p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{o.name} <span className="text-neutral-400 font-normal">· {o.email}</span></p>
                  {o.request && <p className="text-sm text-neutral-500 mt-0.5">Tel: {o.request.phone || "—"} · {o.request.message || "Sin mensaje"}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`approve-req-${o.user_id}`} onClick={() => approveReq(o.user_id)} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-lg"><CheckCircle2 size={14} className="mr-1" />Habilitar</Button>
                  <Button size="sm" variant="outline" data-testid={`reject-req-${o.user_id}`} onClick={() => rejectReq(o.user_id)} className="rounded-lg text-red-600 border-red-200"><Ban size={14} className="mr-1" />Rechazar</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-display font-bold text-xl mb-4">Solicitudes de ampliación</h2>
      <div className="mb-8" data-testid="upgrade-requests">
        {upReqs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 text-neutral-400 text-sm">No hay solicitudes de ampliación.</div>
        ) : (
          <div className="space-y-3">
            {upReqs.map((u) => (
              <div key={u.tenant_id} data-testid={`upreq-${u.tenant_id}`} className="bg-white rounded-2xl border border-sky-200 p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{u.name} <span className="text-neutral-400 font-normal">· {u.email}</span></p>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    Actual: {u.current_active_raffles}/{u.max_active_raffles} rifas · {u.max_numbers_per_raffle} núm. — Pide: {u.requested_active_raffles || "—"} rifas · {u.requested_numbers || "—"} núm.
                  </p>
                  {u.message && <p className="text-sm text-neutral-600 mt-1 italic">"{u.message}"</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`upreq-edit-${u.tenant_id}`} onClick={() => openEditByTenant(u.tenant_id)} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-lg"><SlidersHorizontal size={14} className="mr-1" />Ajustar límites</Button>
                  <Button size="sm" variant="outline" data-testid={`upreq-resolve-${u.tenant_id}`} onClick={() => resolveUpgrade(u.tenant_id)} className="rounded-lg"><CheckCircle2 size={14} className="mr-1" />Marcar resuelta</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-display font-bold text-xl mb-4">Rifas de administradores</h2>      <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-8" data-testid="admin-raffles-section">
        <div className="relative mb-4 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="raffle-search" value={rSearch} onChange={(e) => setRSearch(e.target.value)} placeholder="Buscar por código semilla o título..." className="pl-9 rounded-lg" />
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[{ v: "all", label: "Todos" }, { v: "closed", label: "Cerradas" }, { v: "cancelled", label: "Cancelados" }].map((f) => (
            <button key={f.v} data-testid={`raffle-filter-${f.v}`} onClick={() => setRFilter(f.v)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${rFilter === f.v ? "bg-[#00AEEF] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
              {f.label}
              {f.v !== "all" && <span className="ml-1.5 opacity-70">{adminRaffles.filter((r) => r.status === f.v).length}</span>}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 text-left">
              <tr>
                <th className="px-3 py-2.5 font-medium">Código</th>
                <th className="px-3 py-2.5 font-medium">Rifa</th>
                <th className="px-3 py-2.5 font-medium">Administrador</th>
                <th className="px-3 py-2.5 font-medium">Vendidos</th>
                <th className="px-3 py-2.5 font-medium">Estado</th>
                <th className="px-3 py-2.5 font-medium text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {shownRaffles.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-400">{rFilter === "cancelled" ? "No hay eventos cancelados." : "No se encontraron rifas."}</td></tr>
              ) : shownRaffles.map((r) => (
                <tr key={r.id} className="border-t border-neutral-100" data-testid={`admin-raffle-${r.seed_code}`}>
                  <td className="px-3 py-2.5 font-mono font-bold tracking-wider text-neutral-700">{r.seed_code}</td>
                  <td className="px-3 py-2.5 font-medium max-w-[220px] truncate">{r.title}</td>
                  <td className="px-3 py-2.5 text-neutral-500">{r.organizer_name}<span className="block text-xs text-neutral-400">{r.organizer_email}</span></td>
                  <td className="px-3 py-2.5">{r.sold} / {r.total_numbers}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColor(r.status)}`}>{statusLabel(r.status)}</span></td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.status === "cancelled" && (
                      <Button size="sm" variant="outline" data-testid={`admin-reactivate-raffle-${r.seed_code}`} onClick={() => setReactRaffle(r)}
                        className="rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-2"><RotateCcw size={14} className="mr-1" />Reactivar</Button>
                    )}
                    {r.status === "closed" && (
                      <Button size="sm" variant="outline" data-testid={`admin-resume-raffle-${r.seed_code}`} onClick={() => setReactRaffle(r)}
                        className="rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-2"><RotateCcw size={14} className="mr-1" />Reanudar venta</Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`admin-delete-raffle-${r.seed_code}`} onClick={() => setDelRaffle(r)} className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={14} className="mr-1" />Eliminar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="font-display font-bold text-xl mb-4">Organizadores</h2>
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input data-testid="org-search" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="Buscar organizador por nombre o correo..." className="pl-9 rounded-lg" />
      </div>
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {/* Desktop: tabla (sin cambios de diseño) */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-neutral-50 text-neutral-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Rifas activas</th>
              <th className="px-4 py-3 font-medium">Máx. números</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-400" data-testid="org-empty-desktop">No se encontraron organizadores.</td></tr>
            ) : filteredOrgs.map((o) => (
              <tr key={o.user_id} className="border-t border-neutral-100" data-testid={`org-row-${o.user_id}`}>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    {o.is_super_admin ? (
                      <span>{o.name} <span className="text-xs text-[#00AEEF]">(admin)</span></span>
                    ) : (
                      <button type="button" data-testid={`org-name-${o.user_id}`} onClick={() => setEditOrg({ ...o })} className="font-medium text-neutral-900 text-left hover:text-[#0092CC] hover:underline">{o.name}</button>
                    )}
                    <RoleBadge role={o.role} uid={o.user_id} />
                    {!o.is_super_admin && o.role !== "assistant" && <PlanBadge plan={o.plan} uid={o.user_id} />}
                  </div>
                </td>
                <td className="px-4 py-3 text-neutral-500">{o.email}</td>
                <td className="px-4 py-3">
                  {o.is_super_admin ? "—" : <span data-testid={`org-limits-${o.user_id}`}><b>{o.active_raffle_count ?? 0}</b> / {o.max_active_raffles ?? 1}</span>}
                </td>
                <td className="px-4 py-3">{o.is_super_admin ? "—" : (o.max_numbers_per_raffle ?? 50)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${o.status === "suspended" ? "bg-red-100 text-red-700" : o.status === "pending_approval" ? "bg-amber-100 text-amber-700" : o.status === "rejected" ? "bg-neutral-200 text-neutral-600" : "bg-[#E6F7FE] text-[#0092CC]"}`}>
                    {o.status === "suspended" ? "Suspendido" : o.status === "pending_approval" ? "Pendiente" : o.status === "rejected" ? "Rechazado" : "Activo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {!o.is_super_admin && (
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" data-testid={`edit-limits-${o.user_id}`} onClick={() => setEditOrg({ ...o })} className="rounded-lg"><SlidersHorizontal size={14} className="mr-1" />Límites</Button>
                      <Button size="sm" variant="outline" data-testid={`simulate-${o.user_id}`} onClick={() => { startSim(o); navigate("/dashboard"); }} className="rounded-lg text-[#0092CC] border-[#00AEEF]/40 hover:bg-[#E6F7FE]"><Eye size={14} className="mr-1" />Simular Vista</Button>
                      <Button size="sm" variant="outline" data-testid={`toggle-org-${o.user_id}`} onClick={() => toggle(o)} className="rounded-lg">
                        {o.status === "suspended" ? <><CheckCircle2 size={14} className="mr-1" />Activar</> : <><Ban size={14} className="mr-1" />Suspender</>}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`delete-org-${o.user_id}`} onClick={() => setDeleteOrg(o)} className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={14} className="mr-1" />Eliminar</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Móvil: tarjetas apiladas */}
        <div className="md:hidden divide-y divide-neutral-100">
          {filteredOrgs.length === 0 ? (
            <div className="p-6 text-center text-neutral-400 text-sm" data-testid="org-empty-mobile">No se encontraron organizadores.</div>
          ) : filteredOrgs.map((o) => {
            const st = o.status === "suspended" ? { t: "Suspendido", c: "bg-red-100 text-red-700" } : o.status === "pending_approval" ? { t: "Pendiente", c: "bg-amber-100 text-amber-700" } : o.status === "rejected" ? { t: "Rechazado", c: "bg-neutral-200 text-neutral-600" } : { t: "Activo", c: "bg-[#E6F7FE] text-[#0092CC]" };
            return (
              <div key={o.user_id} data-testid={`org-card-${o.user_id}`} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {o.is_super_admin ? (
                      <p className="font-semibold truncate">{o.name} <span className="text-xs text-[#00AEEF]">(admin)</span></p>
                    ) : (
                      <button type="button" data-testid={`org-name-mobile-${o.user_id}`} onClick={() => setEditOrg({ ...o })} className="font-semibold text-left text-neutral-900 hover:text-[#0092CC] hover:underline truncate max-w-full block">{o.name}</button>
                    )}
                    <p className="text-sm text-neutral-500 truncate">{o.email}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <RoleBadge role={o.role} uid={`m-${o.user_id}`} />
                    {!o.is_super_admin && o.role !== "assistant" && <PlanBadge plan={o.plan} uid={`m-${o.user_id}`} />}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div className="rounded-lg bg-neutral-50 p-2">
                    <p className="text-[11px] text-neutral-400">Rifas activas</p>
                    <p className="text-sm font-semibold mt-0.5">{o.is_super_admin ? "—" : <span data-testid={`org-limits-mobile-${o.user_id}`}>{o.active_raffle_count ?? 0}/{o.max_active_raffles ?? 1}</span>}</p>
                  </div>
                  <div className="rounded-lg bg-neutral-50 p-2">
                    <p className="text-[11px] text-neutral-400">Máx. números</p>
                    <p className="text-sm font-semibold mt-0.5">{o.is_super_admin ? "—" : (o.max_numbers_per_raffle ?? 50)}</p>
                  </div>
                  <div className="rounded-lg bg-neutral-50 p-2 flex flex-col items-center justify-center">
                    <p className="text-[11px] text-neutral-400">Estado</p>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full mt-0.5 ${st.c}`}>{st.t}</span>
                  </div>
                </div>
                {!o.is_super_admin && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button size="sm" variant="outline" data-testid={`edit-limits-mobile-${o.user_id}`} onClick={() => setEditOrg({ ...o })} className="rounded-lg justify-center"><SlidersHorizontal size={14} className="mr-1" />Límites</Button>
                    <Button size="sm" variant="outline" data-testid={`simulate-mobile-${o.user_id}`} onClick={() => { startSim(o); navigate("/dashboard"); }} className="rounded-lg justify-center text-[#0092CC] border-[#00AEEF]/40 hover:bg-[#E6F7FE]"><Eye size={14} className="mr-1" />Simular Vista</Button>
                    <Button size="sm" variant="outline" data-testid={`toggle-org-mobile-${o.user_id}`} onClick={() => toggle(o)} className="rounded-lg justify-center">
                      {o.status === "suspended" ? <><CheckCircle2 size={14} className="mr-1" />Activar</> : <><Ban size={14} className="mr-1" />Suspender</>}
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`delete-org-mobile-${o.user_id}`} onClick={() => setDeleteOrg(o)} className="rounded-lg justify-center text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={14} className="mr-1" />Eliminar</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editOrg} onOpenChange={(v) => !v && setEditOrg(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Editar límites</DialogTitle>
            <DialogDescription>Ajusta el máximo de rifas activas y de números por rifa para esta cuenta.</DialogDescription>
          </DialogHeader>
          {editOrg && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-500">{editOrg.email}</p>
              <div>
                <Label className="text-sm">Nombre</Label>
                <Input data-testid="edit-admin-name" value={editOrg.name || ""} onChange={(e) => setEditOrg({ ...editOrg, name: e.target.value })} className="mt-1 rounded-lg" />
              </div>
              <div>
                <Label className="text-sm">Nivel de acceso</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  <button type="button" data-testid="edit-plan-free" onClick={() => setEditOrg({ ...editOrg, plan: "free", max_active_raffles: 1, max_numbers_per_raffle: 100, max_assistants: 0, max_photos: 3 })}
                    className={`rounded-lg border p-2 text-sm transition-colors ${editOrg.plan === "free" || !editOrg.plan ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Free</button>
                  <button type="button" data-testid="edit-plan-full" onClick={() => setEditOrg({ ...editOrg, plan: "full", max_active_raffles: 3, max_numbers_per_raffle: 2000, max_assistants: 3, max_photos: 5 })}
                    className={`rounded-lg border p-2 text-sm transition-colors ${editOrg.plan === "full" ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Full</button>
                  <button type="button" data-testid="edit-plan-pro" onClick={() => setEditOrg({ ...editOrg, plan: "pro", max_active_raffles: 5, max_numbers_per_raffle: 9000, max_assistants: 5, max_photos: 10 })}
                    className={`rounded-lg border p-2 text-sm transition-colors ${editOrg.plan === "pro" ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Pro</button>
                  <button type="button" data-testid="edit-plan-custom" onClick={() => setEditOrg({ ...editOrg, plan: "custom", custom_perms: editOrg.custom_perms || { can_create_assistants: false, can_download_csv: false, can_view_history: false, adv_stats: false, white_label: false, qr_tickets: false }, max_active_raffles: 9999, max_numbers_per_raffle: 10000, max_assistants: 9999, max_photos: 10 })}
                    className={`rounded-lg border p-2 text-sm transition-colors ${editOrg.plan === "custom" ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Custom</button>
                </div>
                {editOrg.plan === "custom" && (
                  <div className="mt-2 space-y-1.5 rounded-lg border border-neutral-200 p-2" data-testid="custom-perms-block">
                    {[["can_create_assistants", "Crear asistentes"], ["can_download_csv", "Descargar CSV de clientes"], ["can_view_history", "Ver historial"], ["adv_stats", "Estadísticas avanzadas"], ["white_label", "Marca blanca (sin MCTECH)"], ["qr_tickets", "Tickets con QR"]].map(([k, lbl]) => {
                      const on = !!(editOrg.custom_perms || {})[k];
                      return (
                        <button key={k} type="button" data-testid={`custom-perm-${k}`}
                          onClick={() => setEditOrg({ ...editOrg, custom_perms: { ...(editOrg.custom_perms || {}), [k]: !on } })}
                          className="w-full flex items-center justify-between rounded-md px-2.5 py-2 text-sm hover:bg-neutral-50">
                          <span>{lbl}</span>
                          <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? "bg-[#00AEEF]" : "bg-neutral-300"}`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-neutral-400 mt-1">Al elegir un plan se autocompletan los límites; puedes ajustarlos abajo. Custom habilita solo lo que actives aquí.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Máx. rifas activas</Label>
                  <Input data-testid="edit-max-raffles" type="number" min={1} value={editOrg.max_active_raffles ?? 1} onChange={(e) => setEditOrg({ ...editOrg, max_active_raffles: e.target.value })} className="mt-1 rounded-lg" />
                </div>
                <div>
                  <Label className="text-sm">Máx. números por rifa</Label>
                  <Input data-testid="edit-max-numbers" type="number" min={1} value={editOrg.max_numbers_per_raffle ?? 50} onChange={(e) => setEditOrg({ ...editOrg, max_numbers_per_raffle: e.target.value })} className="mt-1 rounded-lg" />
                </div>
                <div>
                  <Label className="text-sm">Máx. asistentes</Label>
                  <Input data-testid="edit-max-assistants" type="number" min={0} value={editOrg.max_assistants ?? 0} onChange={(e) => setEditOrg({ ...editOrg, max_assistants: e.target.value })} className="mt-1 rounded-lg" />
                </div>
                <div>
                  <Label className="text-sm">Máx. fotos por evento</Label>
                  <Input data-testid="edit-max-photos" type="number" min={1} value={editOrg.max_photos ?? 3} onChange={(e) => setEditOrg({ ...editOrg, max_photos: e.target.value })} className="mt-1 rounded-lg" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrg(null)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="save-limits-btn" onClick={saveEdit} disabled={savingEdit} className="rounded-xl bg-[#00AEEF] hover:bg-[#0092CC]">{savingEdit ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteOrg} onOpenChange={(v) => !v && setDeleteOrg(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Eliminar administrador</DialogTitle>
            <DialogDescription>Esta acción es permanente y no se puede deshacer.</DialogDescription>
          </DialogHeader>
          {deleteOrg && (
            <div className="text-sm text-neutral-600 space-y-2">
              <p>Vas a eliminar por completo la cuenta de <b>{deleteOrg.name}</b> ({deleteOrg.email}).</p>
              <p className="text-red-600">Se borrarán también todas sus rifas, números, pedidos, clientes y asistentes de equipo.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrg(null)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="confirm-delete-btn" onClick={confirmDelete} disabled={deleting} className="rounded-xl bg-red-600 hover:bg-red-700">{deleting ? "Eliminando..." : "Eliminar definitivamente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!delRaffle}
        onOpenChange={(v) => !v && setDelRaffle(null)}
        title="¿Desea realmente realizar esta acción?"
        description={delRaffle ? `Se eliminará la rifa "${delRaffle.title}" (código ${delRaffle.seed_code}) del administrador ${delRaffle.organizer_name}, con sus ${delRaffle.sold} venta(s), todos sus números y pedidos. Esta acción no se puede deshacer.` : ""}
        confirmText="Sí, eliminar rifa"
        onConfirm={confirmDeleteRaffle}
        loading={delRaffleLoading}
      />
      <ConfirmDialog
        open={!!reactRaffle}
        onOpenChange={(v) => !v && setReactRaffle(null)}
        title={reactRaffle?.status === "closed" ? "¿Reanudar la venta de esta rifa?" : "¿Reactivar este evento cancelado?"}
        description={reactRaffle ? (reactRaffle.status === "closed"
          ? `La rifa "${reactRaffle.title}" (código ${reactRaffle.seed_code}) de ${reactRaffle.organizer_name} volverá a estar ACTIVA y el público podrá comprar números otra vez. Se conservan sus ${reactRaffle.sold} venta(s), participantes, pagos y toda la configuración original.`
          : `El evento "${reactRaffle.title}" (código ${reactRaffle.seed_code}) de ${reactRaffle.organizer_name} volverá a estar visible para el público como "${statusLabel(reactRaffle.prev_status || "active")}". Se conservan sus ${reactRaffle.sold} venta(s), participantes, pagos y toda la configuración original.`) : ""}
        confirmText={reactRaffle?.status === "closed" ? "Sí, reanudar venta" : "Sí, reactivar"}
        onConfirm={confirmReactivate}
        loading={reactLoading}
      />
    </DashboardLayout>
  );
}
